import React from 'react';
import { GlobalOutlined, ShoppingOutlined, ClockCircleOutlined, CheckCircleOutlined, TruckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ListingDetailsData } from '../hooks/useListingDetails';

type Props = Pick<
  ListingDetailsData,
  'isImported' | 'importCountry' | 'shippingIncluded' | 'importAvailability' | 'estimatedArrivalDate'
>;

const formatArrival = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
};

const ImportedVehicleSection: React.FC<Props> = ({
  isImported,
  importCountry,
  shippingIncluded,
  importAvailability,
  estimatedArrivalDate,
}) => {
  const { t } = useTranslation();
  if (!isImported) return null;

  const isReadyToImport = importAvailability === 'READY_TO_IMPORT';
  const arrival = formatArrival(estimatedArrivalDate);

  const items = [
    {
      icon: <GlobalOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />,
      label: t('listing_details.origin_country'),
      value: importCountry ?? '—',
    },
    {
      icon: <TruckOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />,
      label: t('listing_details.shipping'),
      value: shippingIncluded ? t('listing_details.shipping_included') : t('listing_details.shipping_not_included'),
      highlight: shippingIncluded,
    },
    {
      icon: isReadyToImport
        ? <ClockCircleOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />
        : <CheckCircleOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />,
      label: t('listing_details.availability'),
      value: isReadyToImport ? t('listing_details.ready_to_import') : t('listing_details.available_at_showroom'),
    },
    ...(isReadyToImport && arrival
      ? [{
          icon: <ShoppingOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />,
          label: t('listing_details.expected_arrival'),
          value: arrival,
        }]
      : []),
  ];

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        border: '1px solid #bfdbfe',
        borderRadius: 12,
        padding: '20px 24px',
        marginTop: 8,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div
          style={{
            background: '#1d4ed8',
            borderRadius: 8,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <GlobalOutlined style={{ color: '#fff', fontSize: 16 }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e3a8a' }}>{t('listing_details.imported_vehicle')}</div>
          {importCountry && (
            <div style={{ fontSize: 12, color: '#3b82f6' }}>{t('listing_details.imported_from', { country: importCountry })}</div>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((item) => (
          <div
            key={item.label}
            style={{
              background: '#fff',
              border: '1px solid #bfdbfe',
              borderRadius: 10,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {item.icon}
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {item.label}
              </span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a' }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImportedVehicleSection;
